import React, { useContext } from 'react';
import { Redirect } from 'react-router-dom';
import { startCase } from 'lodash';
import { Page } from 'components';
import { AuthContext } from 'contexts';
import { MLSStaffMenuOptionsType } from 'components/SideMenu/MenuSection/types';
import { LockboxPermissionsManagerModal } from 'modals';
import bulkAssignSupraLockboxForSingleUser from 'api/SupraLockboxController/bulkAssignSupraLockboxForSingleUser';
import bulkCreateShacklePermissions from 'api/SupraLockboxController/bulkCreateShacklePermissions';
import bulkUnassignSupraLockboxes from 'api/SupraLockboxController/bulkUnassignSupraLockboxes';
import MLSStaffRightHeader from './MLSStaffRightHeader';
import MLSStaffBrokeragesTable from './MLSStaffBrokeragesPage/MLSStaffBrokeragesTable';
import { MLSStaffContextProvider, useMLSStaffContext } from './MLSStaffContext';
import MLSStaffUsersTable from './MLSStaffUsersPage/MLSStaffUsersTable';
import MLSStaffLockboxesTable from './MLSStaffLockboxesPage/MLSStaffLockboxesTable';

const MLSStaffPage: React.FC<{ type: MLSStaffMenuOptionsType }> = ({
  type,
}) => {
  const { auth } = useContext(AuthContext);
  if (auth?.org?.type !== 'mlsStaff') {
    return <Redirect to="/listing/list/brokerage/0" />;
  }

  let child: React.ReactNode = null;
  switch (type) {
    case 'brokerages':
      child = <MLSStaffBrokeragesTable />;
      break;
    case 'users':
      child = <MLSStaffUsersTable />;
      break;
    case 'lockboxes':
      child = <MLSStaffLockboxesTable />;
      break;
    default:
      break;
  }

  return (
    <MLSStaffContextProvider>
      <MLSStaffPageInner child={child} type={type} />
    </MLSStaffContextProvider>
  );
};

const MLSStaffPageInner: React.FC<{
  child: React.ReactNode;
  type: MLSStaffMenuOptionsType;
}> = ({ child, type }) => {
  const { auth } = useContext(AuthContext);
  const { lockboxPermissionsModalVisible, setLockboxPermissionsModalVisible } =
    useMLSStaffContext();

  return (
    <>
      <Page
        type="filled"
        title={startCase(type)}
        fillScreenHeight
        headerExtra={<MLSStaffRightHeader />}
      >
        {child}
      </Page>
      <LockboxPermissionsManagerModal
        visible={lockboxPermissionsModalVisible}
        loggedInUser={auth}
        onConfirmAndProcess={async payload => {
          const {
            mode,
            assignMode,
            targetId,
            selectedIds,
            selectedSerialNumbers = [],
            ownerOrAssigneeType,
            lockboxAssigneeMap = {},
            grantOfficeShacklePermissions,
          } = payload;

          try {
            if (mode === 'shackle') {
              if (!targetId) {
                return { successful: 0, unsuccessful: selectedIds.length };
              }
              const result = await bulkCreateShacklePermissions({
                userId:
                  ownerOrAssigneeType === 'office'
                    ? targetId.split('-')[2]
                    : targetId,
                lockboxIds: selectedIds,
                serialNumbers: selectedSerialNumbers,
                ownerOrAssigneeType: ownerOrAssigneeType as 'user' | 'office',
                grantOfficeShacklePermissions,
              });
              return {
                successful: result.successCount,
                unsuccessful: result.failureCount,
              };
            }
            if (mode === 'assignee' && assignMode === 'unassign') {
              if (Object.keys(lockboxAssigneeMap).length === 0) {
                return { successful: 0, unsuccessful: selectedIds.length };
              }

              const result = await bulkUnassignSupraLockboxes({
                lockboxAssigneeMap,
              });
              return {
                successful: result.successCount,
                unsuccessful: result.failureCount,
              };
            }
            if (
              mode === 'owner' ||
              (mode === 'assignee' && assignMode === 'assign')
            ) {
              if (!targetId) {
                return { successful: 0, unsuccessful: selectedIds.length };
              }
              const result = await bulkAssignSupraLockboxForSingleUser({
                userId:
                  ownerOrAssigneeType === 'office'
                    ? targetId.split('-')[2]
                    : targetId,
                lockboxIds: selectedIds,
                permissions: {
                  permissionType: mode as 'owner' | 'assignee',
                  ownerOrAssigneeType: ownerOrAssigneeType as 'user' | 'office',
                  branchIndex:
                    ownerOrAssigneeType === 'office'
                      ? targetId.split('-')[1]
                      : null,
                },
                granteeUserIds: [],
              });
              return {
                successful: result.successCount,
                unsuccessful: result.failureCount,
              };
            }
            return { successful: 0, unsuccessful: selectedIds.length };
          } catch {
            return { successful: 0, unsuccessful: selectedIds.length };
          }
        }}
        onCancel={() => setLockboxPermissionsModalVisible(false)}
      />
    </>
  );
};

export default MLSStaffPage;
